import { MouseEvent, useState, useCallback } from 'react';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Spin, Tooltip, Upload } from 'antd';
import { RcFile } from 'antd/es/upload';
import styled from 'styled-components';
import { themeConfig, useThemeContext } from '@/shared/theme';
import { Row } from '../layout';
import {
  PageTitleBannerAvatarImage,
  PageTitleBannerAvatarWrapper,
} from './PageTitleBannerAvatarImage';

export type PageTitleBannerAvatarUploadProps = {
  imageUrl?: string;
  title?: string;
  isLoading?: boolean;
  onAvatarUpload?: (file: RcFile) => void;
  onAvatarRemove?: () => void;
};

export const PageTitleBannerAvatarUpload = ({
  imageUrl,
  title,
  isLoading,
  onAvatarUpload,
  onAvatarRemove,
}: PageTitleBannerAvatarUploadProps) => {
  const [showBtnOverlay, setShowBtnOverlay] = useState(false);
  const { theme } = useThemeContext();

  const handleRemove = useCallback(
    (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      event.stopPropagation();
      onAvatarRemove?.();
    },
    [onAvatarRemove],
  );

  return (
    <PageTitleBannerAvatarWrapper
      onMouseEnter={() => setShowBtnOverlay(true)}
      onMouseLeave={() => setShowBtnOverlay(false)}
      $borderColor={themeConfig[theme].colors.layoutBg}
    >
      <UploadWrapper
        listType="picture-circle"
        maxCount={1}
        beforeUpload={onAvatarUpload}
        disabled={isLoading}
      >
        <>
          {showBtnOverlay && !isLoading && (
            <>
              <TransparentBackground
                $bgColor={themeConfig[theme].colors.contentBgSecondary}
              />
              <ButtonsWrapper gap={15}>
                <Tooltip title="Upload new image">
                  <IconButton type="text" icon={<UploadOutlined />} />
                </Tooltip>
                {imageUrl && (
                  <Tooltip title="Remove image">
                    <IconButton
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={handleRemove}
                    />
                  </Tooltip>
                )}
              </ButtonsWrapper>
            </>
          )}
          {isLoading && (
            <>
              <Spin style={{ zIndex: 2, position: 'absolute' }} />
              <TransparentBackground
                $bgColor={themeConfig[theme].colors.contentBgSecondary}
              />
            </>
          )}
          <PageTitleBannerAvatarImage
            src={imageUrl}
            title={title}
            showWrapper={false}
          />
        </>
      </UploadWrapper>
    </PageTitleBannerAvatarWrapper>
  );
};

const UploadWrapper = styled(Upload)`
  width: 150px !important;
  height: 150px !important;

  .ant-upload.ant-upload-select {
    width: 142px !important;
    height: 142px !important;
    border: none !important;
  }
  .ant-upload-list-item-container {
    display: none !important;
  }
`;

const ButtonsWrapper = styled(Row)`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  justify-content: center;
`;

const TransparentBackground = styled.div<{ $bgColor?: string }>`
  background: ${({ $bgColor }) => $bgColor};
  opacity: 0.8;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1;
`;

const IconButton = styled(Button)`
  border-radius: 50%;
`;
